
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAvatar } from '@/hooks/useAvatar';
import { useAuth } from '@/contexts/auth';
import { Camera, Upload, Trash2 } from 'lucide-react';

const AvatarUpload: React.FC = () => {
  const { user, profile } = useAuth();
  const { uploadAvatar, removeAvatar, isUploading, avatarUrl } = useAvatar();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Check if user is authenticated with Google
  const isGoogleAuthenticated = !!user?.app_metadata?.provider && 
    user.app_metadata.provider === 'google';
  
  // Generate user initials for avatar fallback
  const getInitials = () => {
    if (profile?.name) {
      return profile.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return user?.email?.substring(0, 2).toUpperCase() || 'U';
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    // Upload to storage
    await uploadAvatar(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveAvatar = async () => {
    await removeAvatar();
    setPreviewUrl(null);
  };
  
  // Use Google avatar if available from user metadata
  const googleAvatarUrl = user?.user_metadata?.avatar_url || null;
  
  // Debug avatar sources in development mode
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AvatarUpload] profile?.avatar_url:', profile?.avatar_url);
      console.log('[AvatarUpload] user?.user_metadata?.avatar_url:', user?.user_metadata?.avatar_url);
      console.log('[AvatarUpload] displayAvatarUrl:', previewUrl || avatarUrl || googleAvatarUrl);
    }
  }, [avatarUrl, googleAvatarUrl, profile?.avatar_url, user?.user_metadata?.avatar_url, previewUrl]);
  
  // Determine which avatar to display in priority order:
  // 1. Preview (just uploaded)
  // 2. Custom uploaded avatar
  // 3. Google avatar
  // 4. Fallback initials
  const displayAvatarUrl = previewUrl || avatarUrl || googleAvatarUrl;
  
  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="h-24 w-24">
        <AvatarImage 
          src={displayAvatarUrl || ''} 
          alt={profile?.name || user?.email || "User"}
        />
        <AvatarFallback className="bg-nation-purple text-white text-2xl">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
      
      {/* Only show avatar upload buttons for non-Google authenticated users */}
      {!isGoogleAuthenticated && (
        <>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex gap-2 items-center"
            >
              {isUploading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                  Uploading...
                </>
              ) : displayAvatarUrl ? (
                <>
                  <Camera className="h-4 w-4" />
                  Change
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
            
            {displayAvatarUrl && !googleAvatarUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveAvatar}
                disabled={isUploading}
                className="text-destructive hover:text-destructive flex gap-1 items-center"
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            )}
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
          </div>
          
          <p className="text-xs text-muted-foreground">
            Upload a profile picture (max 2MB)
          </p>
        </>
      )}

      {/* Show notice for Google authenticated users */}
      {isGoogleAuthenticated && (
        <p className="text-xs text-muted-foreground">
          Your profile picture is managed by Google
        </p>
      )}
    </div>
  );
};

export default AvatarUpload;
